import { useCurrentAccount } from '@application/api';
import {
  PreloadController,
  SeoController,
  setWhatsappBreederId,
  useAuthModal,
  useBuyerChecklistModal,
  useSocialShareModal,
  useWhatsappModal,
} from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import type { PageState } from '@application/types';
import {
  emailShareUrl,
  facebookShareUrl,
  twitterShareUrl,
  useRouter,
  useTranslatedUrl,
  waShareUrl,
} from '@application/utils';
import type { LitterPageProps } from '@presentation/pages';
import { LitterPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Litter/components/PageSkeleton';
import dayjs from 'dayjs';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Analytics } from '@application/utils/analytics';
import { useTranslatableContentState } from '@application/state';
import { useFormatBreeders } from '../BreederListPageController/api/useFormatBreeders';
import { useFormatLitters } from '../LitterListPageController/api/useFormatLitters';
import { useAddBookmark } from './api/useAddBookmark';
import { useFormatLitter } from './api/useFormatLitter';
import { useGetBookmark } from './api/useGetBookmark';
import { useGetBreeders } from './api/useGetBreeders';
import { useGetLitter } from './api/useGetLitter';
import { useGetLitters } from './api/useGetLitters';
import { useRemoveBookmark } from './api/useRemoveBookmark';
import { useSeo } from './useSeo';

export const LitterPageController: FC = () => {
  const { translate, infoFromCurrentPath, routeLocationDescriptor, getLocaleLinksForTranslations } =
    useRouter();
  const { pathname } = useLocation();
  const { show: showShareModal, setUrls } = useSocialShareModal();
  const { modalHook: whatsappModal } = useWhatsappModal();
  const id = useMemo(() => {
    return infoFromCurrentPath.params[1] ?? infoFromCurrentPath.params[0];
  }, [infoFromCurrentPath.params]);

  const { litter, litterLoading, litterError } = useGetLitter(id);

  const { litters, littersLoading } = useGetLitters(litter?.id, litter?.dogBreed?.id);
  const { breeders, breedersLoading } = useGetBreeders(litter?.id, litter?.dogBreed?.id);
  const { formattedLitters } = useFormatLitters(litters);
  const { formattedBreeders } = useFormatBreeders(breeders);

  useTranslatedUrl(litter?.translations);

  const { hasBookmark, bookmarkLoading } = useGetBookmark(litter?.id);
  const { addBookmark, addBookmarkLoading } = useAddBookmark();
  const { removeBookmark, removeBookmarkLoading } = useRemoveBookmark();
  const { setOriginalLang } = useTranslatableContentState();

  const { formattedLitter, imagesToPreload, formattedBreeder, litterLanguage } =
    useFormatLitter(litter);

  // TC-1354
  useEffect(() => {
    if (litterLanguage) {
      setOriginalLang(litterLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { currentAccountLoggedIn, currentAccountType } = useCurrentAccount();
  const { show } = useAuthModal();

  const { show: showBuyerChecklistModal } = useBuyerChecklistModal();

  const pageState = useMemo((): PageState => {
    if (litterLoading) return 'loading';
    if (litterError) return 'error';
    if (!formattedLitter.breedName) return 'empty';
    return 'data';
  }, [formattedLitter.breedName, litterError, litterLoading]);

  const wishlistClickHandler = useCallback(() => {
    if (!litter?.id) return;
    if (!currentAccountLoggedIn) {
      show();
      return;
    }
    if (hasBookmark) removeBookmark(litter.id);
    else addBookmark(litter.id);
  }, [addBookmark, hasBookmark, litter?.id, currentAccountLoggedIn, removeBookmark, show]);

  const { title, description, keywords, canonicalUrl } = useSeo(litter);

  const backLink = useMemo((): LocationDescriptor => {
    return routeLocationDescriptor('litterList', {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
      params: infoFromCurrentPath?.state?.returnBreed ?? [],
    });
  }, [
    infoFromCurrentPath?.state?.returnBreed,
    infoFromCurrentPath?.state?.returnSearch,
    routeLocationDescriptor,
  ]);

  const warning = useMemo((): string | undefined => {
    if (!litter) return;
    if (litter.born || !litter.birthday) return;
    if (dayjs(litter.birthday).add(1, 'month').isBefore(dayjs())) {
      return translate('litterDetails.futureLitterBirthdayWarning');
    }
  }, [litter, translate]);

  const headerSubtitle = useMemo(() => {
    const subtitle = formattedLitter.hasPedigree
      ? translate('glossary.dogWithPedigree')
      : translate('glossary.dogWithoutPedigree');
    if (litter?.born) {
      return subtitle.concat(translate('glossary.litterVerified'));
    }
    return subtitle;
  }, [formattedLitter.hasPedigree, litter?.born, translate]);

  const moreLittersLink = routeLocationDescriptor('litterList', {
    params: [(litter?.dogBreed?.baseBreedName ?? litter?.dogBreed?.name) as string],
  });
  const moreBreedersLink = routeLocationDescriptor('breederList', {
    params: [(litter?.dogBreed?.baseBreedName ?? litter?.dogBreed?.name) as string],
  });

  const socialShareClickHandler = useCallback(() => {
    const url = `https://tipaw.com${pathname}`;

    showShareModal();

    setUrls({
      facebook: facebookShareUrl(url),
      twitter: twitterShareUrl(url, title),
      whatsapp: waShareUrl(url),
      email: emailShareUrl(url, title, `${title}\n\n${description}`),
    });
  }, [description, pathname, setUrls, showShareModal, title]);

  const enableWhatsAppContact = useMemo(() => {
    return !!formattedBreeder?.whatsappNumber;
  }, [formattedBreeder?.whatsappNumber]);

  const onWhatsappClickHandler = useCallback(() => {
    Analytics.clickOnBreederWhatsapp();
    whatsappModal.show();
  }, [whatsappModal]);

  useEffect(() => {
    if (enableWhatsAppContact && formattedBreeder?.id) {
      setWhatsappBreederId(formattedBreeder.id);
    }
  }, [enableWhatsAppContact, formattedBreeder]);

  const showApplyButton = useMemo(() => {
    return !enableWhatsAppContact && currentAccountType !== 'breeder';
  }, [currentAccountType, enableWhatsAppContact]);

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={title}
        description={description}
        keywords={keywords}
        thumbnail={formattedLitter?.slideshow?.items?.find((item) => !item.isVideo)?.src}
        localeLinks={getLocaleLinksForTranslations(litter?.translations)}
        canonical={canonicalUrl}
      />
      {pageState === 'loading' && <PageSkeleton backLink={backLink} />}
      {(pageState === 'empty' || pageState === 'error') && (
        <RedirectWithStatus status={404} to="/404" />
      )}
      {pageState === 'data' && (
        <LitterPage
          warning={warning}
          {...(formattedLitter as Partial<LitterPageProps>)}
          header={{
            title: translate('litterDetails.title').replace(
              '$breed',
              formattedLitter.breedName ?? '-',
            ),
            subtitle: headerSubtitle,
            backLink,
          }}
          showWishlist={currentAccountType === 'buyer'}
          isWishlisted={hasBookmark}
          onWishlistClick={wishlistClickHandler}
          wishlistLoading={addBookmarkLoading || removeBookmarkLoading || bookmarkLoading}
          litterItems={formattedLitters}
          breederItems={formattedBreeders}
          litterItemsLoading={littersLoading}
          breederItemsLoading={breedersLoading}
          moreLittersLink={moreLittersLink}
          moreBreedersLink={moreBreedersLink}
          showApplyButton={showApplyButton}
          onOpenBuyerChecklist={showBuyerChecklistModal}
          onSocialShareClick={socialShareClickHandler}
          enableWhatsAppContact={enableWhatsAppContact}
          onWhatsappClick={onWhatsappClickHandler}
        />
      )}
    </>
  );
};
